import React, { useEffect, useMemo } from "react";
import { SpaceBetween, ContentLayout, Grid } from "@cloudscape-design/components";
import { useNavigate } from "react-router-dom";
import { useUserAlias } from "../../hooks/useUserAlias";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { useGetAllWorkflowExecutions } from "../../api/hooks";
import { transformToActivityItem } from "../../utils/activityTransformUtils";
import { transformWorkflowTemplate } from "../../utils/workflowHelpers";
import SectionHeader from "../common/SectionHeader";
import {
  KeyMetricsSection,
  AgenticFlowSection,
  AccessDataSection,
  ResourcesSection,
  KeyLaunchesSection,
  RecentActivitySection,
  FPACalendarSection,
} from "./sections";
import "./dashboard.scss";
import { logger } from '../../utils/logger';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { alias } = useUserAlias();
  const { selectedSpaceId, fetchSpaces } = useGlobalStore();
  const {
    workflowTemplates,
    fetchWorkflowTemplates,
    workflows,
    fetchWorkflows,
    isTemplatesLoading
  } = useWorkflowStore();
  const { data: executionsData, isLoading: isExecutionsLoading } =
    useGetAllWorkflowExecutions(selectedSpaceId || undefined);

  // Show loading if we don't have a space selected yet OR if templates are loading
  const isAgenticFlowLoading = !selectedSpaceId || isTemplatesLoading;

  // Fetch spaces and workflow templates on mount
  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflowTemplates();
      fetchWorkflows(selectedSpaceId);
    }
  }, [selectedSpaceId, fetchWorkflowTemplates, fetchWorkflows]);

  // Transform API data to recent activity items (latest 5 based on creation time)
  const recentActivityItems = useMemo(() => {
    if (!executionsData?.results || !workflows) {
      return [];
    }

    try {
      return executionsData.results
        .map((execution) => {
          const workflow = workflows.find((w) => w.id === execution.workflowId);
          return transformToActivityItem(execution, workflow);
        })
        .filter((item) => item.createdAt) // Only include items with creation time
        .sort(
          (a, b) =>
            new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
        ) // Sort by creation time desc
        .slice(0, 5); // Take latest 5
    } catch (err) {
      logger.error(
        "Failed to transform workflow executions for recent activity:",
        err as Error
      );
      return [];
    }
  }, [executionsData, workflows]);

  // Use API data - sort alphabetically by name
  const reportTypes = workflowTemplates
    ? workflowTemplates
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map(transformWorkflowTemplate)
    : [];

  // Add navigation to workflow actions
  const enhancedReportTypes = reportTypes.map((type) => ({
    ...type,
    workflowActions: type.workflowActions.map((action) => ({
      ...action,
      href: action.route,
    })),
  }));

  return (
    <ContentLayout
      defaultPadding
      header={
        <SectionHeader
          title={`Hello ${alias || "There"}!`}
          variant="h1"
          useGradient={true}
        />
      }
    >
      <SpaceBetween direction="vertical" size="xxs">
        <KeyMetricsSection />
        <AgenticFlowSection
          reportTypes={enhancedReportTypes}
          loading={isAgenticFlowLoading}
        />
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
          <SpaceBetween size="xxs">
            <AccessDataSection />
            <ResourcesSection />
            <KeyLaunchesSection />
          </SpaceBetween>
          <SpaceBetween size="xxs">
            <RecentActivitySection
              items={recentActivityItems}
              loading={isExecutionsLoading}
            />
            <FPACalendarSection />
          </SpaceBetween>
        </Grid>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default Dashboard;
